import {
    Box,
    Checkbox,
    Paper,
    TextField,
    Chip,
    Stack,
    IconButton,
    Button,
    Typography,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grow,
    MenuItem,
} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useMemo, useRef, useState } from 'react';

// Custom Utils
import helpers from '../../hooks/HelperFunctions';

// Icons
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ReplayIcon from '@mui/icons-material/Replay';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import portalpaymentlink from '../../services/portalpaymentlink';
import Swal from 'sweetalert2';
import AssignVendorModal from './AssignVendorModal';
import PaymentLinkWhatsAppModal from './PaymentLinkWhatsAppModal';
import useAccess from '../../hooks/useAccess';
import { usePaymentLinkChats } from '../../hooks/useWhatsapp';
import { useNavigate } from 'react-router-dom';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CustomConfirms from '../../utils/helpers';
import {
    getDefaultPayableDate,
    formatPayableDate,
} from '../../utils/payableDate';
import SearchField, { filterSelectSx } from '../common/SearchField';
import { formatUsername } from '../plans/influencerFormat';
const STATUS_CONFIG = {
    awaiting_pricing: {
        label: 'Awaiting Pricing',
        color: '#d3ffce',
    },
    locked: {
        label: 'Locked',
        color: '#ced3ff',
    },
    processing: {
        label: 'Processing',
        color: '#ffced3',
    },
    payment_generated: {
        label: 'Generated',
        color: '#ffb598',
    },
    payment_deleted: {
        label: 'Deleted',
        color: '#f6a1a9',
    },
    failed: {
        label: 'Failed',
        color: '#ffface',
    },
    invalid: {
        label: 'Invalid',
        color: '#f08080',
    },
};

// A generated link later merged into a combined (clubbed) link — keyed by that
// combined link's own PaymentLink status (S/F/R; anything else is in progress).
const CLUBBED_STATUS_CONFIG = {
    S: { label: 'Paid (Clubbed)', color: '#b8e6c1' },
    F: { label: 'Clubbed · Failed', color: '#f6a1a9' },
    R: { label: 'Clubbed · Rejected', color: '#f6a1a9' },
    default: { label: 'Clubbed', color: '#ffb598' },
};
// Sentinel for the "no POC assigned" choice — a plain '' can't be used
// because that's already the dropdown's "All POCs" (no filter) value.
const UNASSIGNED_POC = '__unassigned__';

// Keyed on the POC's name, not their uploadedBy id, because the queue
// endpoint projects pocData.fullName only — the name is the one thing the
// dropdown can display. Keying on the id instead would put two entries
// reading "Isha Mishra" in the list whenever two POCs share a name, each
// holding part of the rows and neither tellable from the other. Grouping by
// name merges them, which at worst shows a superset for that one name.
const pocKeyOf = (row) => (row?.pocData?.fullName || '').trim();

function QueueStatus({ queue, isFrozen = false }) {
    const [rows, setRows] = useState(queue ?? []);
    const [selectedRows, setSelectedRows] = useState([]);
    const [vendorAssignModal, setVendorAssignModal] = useState(false);
    const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
    const { hasPermission } = useAccess();
    const navigate = useNavigate();
    // Rows we've already sent a payment link to on WhatsApp get a button that opens
    // that person's chat in the Inbox; every other row shows nothing. (The first row's
    // campaignId stands in for the campaign — the queue is already scoped to one.)
    const { data: chatData } = usePaymentLinkChats(
        queue?.[0]?.campaignId,
        hasPermission('whatsapp_inbox')
    );
    const chatByQueueId = useMemo(
        () => new Map((chatData?.chats || []).map((c) => [c.queueId, c])),
        [chatData]
    );
    const [loadingActions, setLoadingActions] = useState({
        followup: null,
        commonLink: false,
        assignVendor: false,
        lock: null,
        price: false,
    });
    const [datePickerModal, setDatePickerModal] = useState({
        open: false,
        queueId: null,
        selectedDate: new Date(),
        saving: false,
    });
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pocFilter, setPocFilter] = useState('');
    useEffect(() => {
        setRows(queue);
    }, [queue]);

    const timeoutRef = useRef(null);

    function openDatePicker(queueId, currentDate) {
        setDatePickerModal({
            open: true,
            queueId: queueId,
            selectedDate: currentDate
                ? new Date(currentDate)
                : getDefaultPayableDate(),
            saving: false,
        });
    }

    function handleIncludeGST(id, isChecked) {
        setRows((prev) =>
            prev.map((row) => {
                if (row._id !== id) return row;

                const gstAmount = isChecked
                    ? Math.round(Number(row.cost || 0) * 0.18)
                    : 0;

                return {
                    ...row,
                    includeGST: isChecked,
                    gstAmount,
                };
            })
        );
    }

    function recalculateRow(row) {
        const payableAmount = Math.round(
            Number(row.cost || 0) * (Number(row.percentage || 0) / 100)
        );

        const gstAmount = row.includeGST ? Math.round(row.cost * 0.18) : 0;

        return {
            ...row,
            payableAmount,
            gstAmount,
        };
    }

    function handleCostChange(id, amount) {
        amount = Number(amount || 0);

        if (isNaN(amount)) {
            return;
        }

        if (amount < 0) {
            amount = 0;
        }

        setRows((prev) =>
            prev.map((row) => {
                if (row._id !== id) {
                    return row;
                }

                return recalculateRow({
                    ...row,
                    cost: amount,
                });
            })
        );
    }

    function handlePercentageChange(id, percentage) {
        const queueObj = rows.find((row) => row._id === id);

        percentage = Number(percentage || 0);

        const maxPercentage = queueObj?.['remainingPercentage'] ?? 100;
        if (percentage > maxPercentage) {
            percentage = maxPercentage;
        }

        if (percentage < 0) {
            percentage = 0;
        }

        setRows((prev) =>
            prev.map((row) => {
                if (row._id !== id) {
                    return row;
                }

                return recalculateRow({
                    ...row,
                    percentage,
                });
            })
        );
    }

    function checkDisabledState(btnName) {
        // A frozen campaign is fully view-only — this disables every one of
        // these actions in one place, including the two (assign_vendor,
        // common_payment_link) whose backend routes don't yet fetch the
        // Campaign doc to enforce this themselves (see routes/paymentLink.js).
        if (isFrozen) return true;

        const hasSelection = selectedRows.length > 0;

        switch (btnName) {
            case 'assign_vendor':
            case 'delete_selected':
                return !hasSelection;

            case 'lock_selected':
                let missingPriceOrPercentage = false;
                for (let i = 0; i < selectedRows.length; i++) {
                    const paymentQueue = rows.find(
                        (row) => row._id === selectedRows[i]
                    );
                    if (!paymentQueue['cost'] || !paymentQueue['percentage']) {
                        missingPriceOrPercentage = true;
                        break;
                    }
                }
                return !hasSelection || missingPriceOrPercentage;

            case 'create_payment_links':
                let foundNotLockedQueue = false;
                for (let i = 0; i < selectedRows.length; i++) {
                    const paymentQueue = rows.find(
                        (row) => row._id === selectedRows[i]
                    );
                    if (
                        !paymentQueue['isLocked'] ||
                        paymentQueue['status'] !== 'locked'
                    ) {
                        foundNotLockedQueue = true;
                        break;
                    }
                }
                return !hasSelection || foundNotLockedQueue;

            case 'send_whatsapp':
                // Only rows whose payment link already exists can be sent; the
                // server re-checks each one (deleted link, vendor without a
                // phone number, ...) and the dialog lists what it will skip.
                return (
                    !hasSelection ||
                    selectedRows.length > 500 ||
                    selectedRows.some(
                        (id) =>
                            rows.find((row) => row._id === id)?.status !==
                            'payment_generated'
                    )
                );

            case 'common_payment_link':
                let foundNotGenerateQueue = false,
                    vendorIds = new Set(),
                    missingVendor = false;
                for (const id of selectedRows) {
                    const paymentQueue = rows.find((row) => row._id === id);

                    if (paymentQueue['status'] !== 'payment_generated') {
                        foundNotGenerateQueue = true;
                    }

                    if (paymentQueue?.['vendorData']?._id) {
                        vendorIds.add(paymentQueue['vendorData']['_id']);
                    } else {
                        missingVendor = true;
                    }
                }

                return (
                    !hasSelection ||
                    selectedRows.length < 2 ||
                    missingVendor ||
                    foundNotGenerateQueue ||
                    vendorIds.size !== 1
                );

            default:
                return false;
        }
    }
    async function savePayableDate() {
        const { queueId, selectedDate } = datePickerModal;

        if (!queueId) {
            Swal.fire({
                icon: 'warning',
                title: 'Error',
                text: 'No queue selected',
            });
            return;
        }

        try {
            setDatePickerModal((prev) => ({ ...prev, saving: true }));

            const response = await portalpaymentlink.updatePayableDate(
                queueId,
                { payableDate: selectedDate.toISOString() }
            );

            if (response.success) {
                const updatedPayableDate =
                    response?.updatedRow?.payableDate ??
                    selectedDate.toISOString();

                setRows((prev) =>
                    prev.map((row) => {
                        if (row._id === queueId) {
                            return { ...row, payableDate: updatedPayableDate };
                        }
                        return row;
                    })
                );

                Swal.fire({
                    icon: 'success',
                    title: 'Date Updated',
                    text: `Payable Date set to: ${formatPayableDate(updatedPayableDate)}`,
                });

                setDatePickerModal({
                    open: false,
                    queueId: null,
                    selectedDate: new Date(),
                    saving: false,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.message || 'Failed to update date',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    error?.message ||
                    'Failed to update date',
            });
        } finally {
            setDatePickerModal((prev) => ({ ...prev, saving: false }));
        }
    }

    async function updateRowPricing(id) {
        try {
            setLoading('lock', id);
            const paymentRow = rows.find((r) => r._id === id);

            if (
                !paymentRow['payableAmount'] ||
                !paymentRow['cost'] ||
                !paymentRow['percentage']
            ) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Missing Information',
                    text: 'Please set Cost and Percentage before locking',
                });
                return;
            }

            const response = await portalpaymentlink.updatePaymentQueuePricing(
                id,
                {
                    cost: paymentRow['cost'],
                    percentage: paymentRow['percentage'],
                    includeGST: paymentRow['includeGST'],
                }
            );

            if (response['success']) {
                const updatedRowObj = response['updatedRow'];
                setRows((prev) =>
                    prev.map((row) => {
                        if (row._id === id) return { ...row, ...updatedRowObj };
                        return row;
                    })
                );

                if (updatedRowObj?.payableDate) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Price Locked Successfully',
                        text: `Payable Date: ${formatPayableDate(updatedRowObj.payableDate)}`,
                    });
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Lock price error',
                    text: response['message'],
                });
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'There has been an error in locking the price';
            Swal.fire({
                icon: 'error',
                title: 'Lock Payment Error',
                text: errorMessage,
            });
        } finally {
            setLoading('lock', null);
        }
    }

    async function unlockPaymentQueue(id) {
        const paymentRow = rows.find((r) => r._id === id);
        if (paymentRow['isLocked']) {
            const response = await portalpaymentlink.unlockOrDeletePaymentQueue(
                'unlock_queue',
                id
            );
            if (response['success']) {
                const updatedObj = response['queueObj'];
                setRows((prev) =>
                    prev.map((pr) => {
                        if (pr._id === id) return { ...pr, ...updatedObj };
                        else return pr;
                    })
                );
            }
        }
    }

    async function assignVendorToQueue(vendor) {
        let postdata = {
            queueIds: selectedRows,
            vendor: vendor._id,
        };

        const response =
            await portalpaymentlink.assignPaymentQueueToVendor(postdata);

        if (response['success']) {
            setVendorAssignModal(false);

            Swal.fire({
                icon: 'success',
                title: 'Assign vendors',
                text: `Links updated: ${response['modifiedCount']}`,
            });

            setRows((prev) =>
                prev.map((row) => {
                    if (selectedRows.includes(row._id)) {
                        return {
                            ...row,
                            vendorData: {
                                name: vendor?.name,
                                _id: vendor?._id,
                            },
                        };
                    } else {
                        return row;
                    }
                })
            );
        }
    }

    async function getQueueStatus() {
        const response = await portalpaymentlink.fetchQueueStatus(
            { queueIds: selectedRows },
            rows[0]?.campaignId
        );

        console.log('status response: ', response);

        if (response['success']) {
            const { queue, processing, generated, failed } = response;

            setRows((prev) =>
                prev.map((row) => {
                    const exist = queue.find((q) => q._id === row._id);

                    if (exist) {
                        return {
                            ...row,
                            status: exist?.status,
                        };
                    } else {
                        return row;
                    }
                })
            );

            return {
                processing,
                generated,
                failed,
            };
        }
    }

    async function pollStatus() {
        try {
            const response = await getQueueStatus();

            if (!response.processing) {
                return;
            }

            timeoutRef.current = setTimeout(pollStatus, 10000);
        } catch (error) {
            console.error(error);

            timeoutRef.current = setTimeout(pollStatus, 5000);
        }
    }

    async function generatePaymentLinks() {
        try {
            if (selectedRows.length === 0) {
                Swal.fire({
                    title: 'Generate payment links',
                    text: 'No links selected',
                    icon: 'error',
                });

                return;
            }

            let postdata = {
                queueIds: selectedRows,
                campaignId: rows[0]?.campaignId,
            };

            const response =
                await portalpaymentlink.generatePaymentLinks(postdata);
            console.log(`response: `, response);

            if (response['success']) {
                Swal.fire({
                    title: 'Generate payment links',
                    text: response['message'],
                    icon: 'success',
                });

                pollStatus();
            }
        } catch (error) {
            console.error(error);
        }
    }

    async function createFollowupPayment(id) {
        try {
            setLoading('followup', id);

            const response =
                await portalpaymentlink.createFollowUpPaymentQueue(id);
            console.log(`response: `, response);

            if (response?.success) {
                const details = response?.details;

                const parentIndex = rows.findIndex((row) => row._id === id);
                const newRows = [...rows];

                newRows.splice(parentIndex + 1, 0, details);

                setRows(newRows);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'There has been an error in creating follow up queue';
            Swal.fire({
                title: 'Follow up payment queue',
                text: errorMessage,
                icon: 'error',
            });
        } finally {
            setLoading('followup', null);
        }
    }

    async function deletePaymentQueue(id) {
        try {
            const deleteCheck = await Swal.fire({
                title: 'Are you sure want to delete this queue',
                text: "You won't be able to revert this",
                confirmButtonText: 'Yes, Delete',
                cancelButtonText: 'No, Cancel',
                showConfirmButton: true,
                showCancelButton: true,
            });

            if (!deleteCheck.isConfirmed) {
                return;
            }

            const response = await portalpaymentlink.unlockOrDeletePaymentQueue(
                'delete',
                id
            );

            if (response?.success) {
                setRows((prev) => prev.filter((row) => row._id !== id));

                Swal.fire({
                    title: 'Queue Deleted',
                    text: 'Queue has been deleted successfully',
                    icon: 'success',
                });

                return;
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'There has been an error while deleting the payment queue.';
            Swal.fire({
                title: 'Queue Deletion',
                text: errorMessage,
                icon: 'error',
            });
        }
    }

    function handleSelectRow(id) {
        setSelectedRows((prev) => {
            if (prev.includes(id)) {
                return prev.filter((rowId) => rowId !== id);
            }

            return [...prev, id];
        });
    }

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    const columns = [
        {
            field: 'select',
            headerName: '#',
            width: 80,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        columnGap={0.5}
                    >
                        <Checkbox
                            checked={selectedRows.includes(params.row._id)}
                            onChange={() => handleSelectRow(params.row._id)}
                            size="small"
                        />

                        <Typography fontSize={12}>
                            {filteredRows.findIndex(
                                (r) => r._id === params.row._id
                            ) + 1}
                        </Typography>
                    </Stack>
                );
            },
        },
        {
            field: 'username',
            headerName: 'Creator',
            // Room for the WhatsApp icon once any chat exists.
            width: chatByQueueId.size > 0 ? 185 : 150,
            renderCell: (params) => {
                const sourceReportNames = params.row.sourceReportNames || [];
                // Only rows a payment link was already sent to on WhatsApp get the icon.
                const chat = chatByQueueId.get(params.row._id);

                return (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box
                            sx={{
                                bgcolor: 'transparent',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                            }}
                        >
                            <Typography color={'#3730a3'} fontSize={12}>
                                {formatUsername(params.row.username) || params.row.username}
                            </Typography>
                        </Box>

                        {sourceReportNames.length > 0 && (
                            <Tooltip
                                title={`via Report${sourceReportNames.length > 1 ? 's' : ''}: ${sourceReportNames.join(', ')}`}
                            >
                                <DescriptionOutlinedIcon
                                    sx={{ fontSize: 14, color: '#7C6FF7' }}
                                />
                            </Tooltip>
                        )}

                        {chat && (
                            <Tooltip
                                title={`Open WhatsApp chat with ${
                                    chat.recipientType === 'vendor'
                                        ? 'vendor'
                                        : 'creator'
                                }`}
                            >
                                <IconButton
                                    size="small"
                                    sx={{ p: 0.25 }}
                                    onClick={() =>
                                        navigate('/whatsapp/inbox', {
                                            state: {
                                                conversationId:
                                                    chat.conversationId,
                                            },
                                        })
                                    }
                                >
                                    <WhatsAppIcon
                                        sx={{ fontSize: 18, color: '#25D366' }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                );
            },
        },
        {
            field: 'vendorData',
            headerName: 'Vendor',
            width: 130,
            renderCell: (params) => {
                if (!params?.row?.vendorData?.name) {
                    return <></>;
                }

                return (
                    <Box
                        sx={{
                            bgcolor: 'transparent',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                        }}
                    >
                        <Typography color={'#7e22ce'} fontSize={12}>
                            {params?.row?.vendorData?.name}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'fullName',
            headerName: 'POC',
            width: 120,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (params) => params?.row?.pocData?.fullName,
        },
        {
            field: 'followers',
            headerName: 'Followers',
            width: 80,
            align: 'right',
            headerAlign: 'right',
            valueGetter: (params) =>
                helpers.formatNumber(params?.row?.snapshot?.followers) ?? 0,
        },
        {
            field: 'averageViews',
            headerName: 'Avg. Views',
            width: 90,
            align: 'right',
            headerAlign: 'right',
            valueGetter: (params) =>
                helpers.formatNumber(params?.row?.snapshot?.averageViews) ?? 0,
        },
        {
            field: 'postCount',
            headerName: 'Posts',
            width: 60,
            align: 'right',
            headerAlign: 'right',
            valueGetter: ({ row }) => row?.postMeta?.length ?? 0,
        },
        {
            field: 'cost',
            headerName: 'Total Amount ✏️',
            width: 140,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <TextField
                    size="small"
                    type="number"
                    value={params.row.cost === 0 ? '' : params.row.cost}
                    onChange={(event) =>
                        handleCostChange(params.row._id, event.target.value)
                    }
                    sx={{ width: '100%', fontSize: '12px' }}
                    placeholder="Amount"
                    inputProps={{
                        min: 0,
                        style: {
                            width: '100%',
                            fontSize: '12px',
                        },
                    }}
                    onKeyDown={(event) => {
                        if (
                            event.key === '.' ||
                            event.key === '-' ||
                            event.key === 'e'
                        ) {
                            event.preventDefault();
                        }
                    }}
                    disabled={params.row.isLocked || isFrozen}
                />
            ),
            valueFormatter: ({ value }) => helpers.formatNumber(value, true),
        },
        {
            field: 'percentage',
            headerName: 'Percentage ✏️',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) =>
                params.row.status === 'payment_generated' ? (
                    <Chip
                        size="small"
                        label={`${params.row.percentage}%`}
                        sx={{
                            fontSize: '11px',
                            borderRadius: '6px',
                            width: '100%',
                            bgcolor: '#ffcd29',
                        }}
                    />
                ) : (
                    <TextField
                        size="small"
                        type="number"
                        value={
                            params.row.percentage === 0
                                ? ''
                                : params.row.percentage
                        }
                        onChange={(event) =>
                            handlePercentageChange(
                                params.row._id,
                                event.target.value
                            )
                        }
                        inputProps={{
                            min: 0,
                            max: params.row.remainingPercentage || 100,
                            style: {
                                width: '100%',
                                fontSize: '12px',
                            },
                        }}
                        placeholder="%"
                        onKeyDown={(event) => {
                            if (
                                event.key === '.' ||
                                event.key === '-' ||
                                event.key === 'e'
                            ) {
                                event.preventDefault();
                            }
                        }}
                        disabled={params.row.isLocked || isFrozen}
                    />
                ),
        },
        {
            field: 'includeGST',
            headerName: 'Add GST ✏️',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Checkbox
                    checked={params?.value ?? false}
                    onChange={(event) =>
                        handleIncludeGST(params.row._id, event.target.checked)
                    }
                    title="Include GST"
                    size="small"
                    disabled={params.row.isLocked || isFrozen}
                />
            ),
        },
        {
            field: 'payableAmount',
            headerName: 'Payable Amt.',
            width: 100,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => {
                const payableAmt = params?.row?.payableAmount;
                if (!payableAmt) return;

                return (
                    <Box
                        sx={{
                            bgcolor: '#198754',
                            px: 0.9,
                            py: 0.25,
                            borderRadius: 1,
                        }}
                    >
                        <Typography color={'#FFFFFF'} fontSize={11}>
                            {helpers.formatNumber(payableAmt, true)}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'payableDate',
            headerName: 'Payable Date',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const dateValue = params?.row?.payableDate;
                if (!dateValue) {
                    return (
                        <Typography fontSize={12} color="text.secondary">
                            -
                        </Typography>
                    );
                }
                return (
                    <Typography fontSize={12}>
                        {formatPayableDate(dateValue)}
                    </Typography>
                );
            },
        },
        {
            field: 'gstAmount',
            headerName: 'GST Amt.',
            width: 90,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => {
                const gstAmt = params?.row?.gstAmount;
                if (!gstAmt) return;

                return (
                    <Box
                        sx={{
                            bgcolor: '#198754',
                            px: 0.9,
                            py: 0.25,
                            borderRadius: 1,
                        }}
                    >
                        <Typography color={'#FFFFFF'} fontSize={11}>
                            {helpers.formatNumber(gstAmt, true)}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 140,
            renderCell: (params) => {
                const status = params.value;
                const chip = STATUS_CONFIG[status] || STATUS_CONFIG.invalid;
                // Link was merged into a combined (clubbed) link — show that link's state instead.
                const clubbed = status === 'payment_generated' && params.row.clubbedInto;
                const clubbedChip = clubbed && (CLUBBED_STATUS_CONFIG[clubbed.status] || CLUBBED_STATUS_CONFIG.default);
                const { label, color } = clubbedChip || chip;

                return (
                    <Chip
                        size="small"
                        label={label}
                        title={clubbed ? `Merged into a combined payment link${clubbed.campaignName ? ` (${clubbed.campaignName})` : ''}` : undefined}
                        sx={{
                            background: color,
                            fontSize: '11px',
                            px: 0.6,
                            py: 0.25,
                            borderRadius: '6px',
                        }}
                    />
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params) => {
                const status = params.row.status;
                const paymentLinkId = params?.row?.paymentLinkId;

                const actionButtonSx = {
                    transition:
                        'transform 0.15s ease, background-color 0.15s ease',
                    '&:hover': {
                        transform: 'scale(1.15)',
                    },
                };

                // Every action below (date edit, lock/unlock, follow-up
                // payment, delete) mutates this row — a frozen campaign is
                // view-only, so the whole cell is empty rather than showing
                // controls that would just 423 on click.
                if (isFrozen) return null;

                return (
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={0.5}
                    >
                        {(status === 'awaiting_pricing' ||
                            status === 'locked') && (
                            <Tooltip title="Edit Payable Date">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        openDatePicker(
                                            params.row._id,
                                            params.row.payableDate
                                        )
                                    }
                                    sx={actionButtonSx}
                                >
                                    <DateRangeIcon
                                        sx={{ fontSize: 16, color: '#1976d2' }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}

                        {status === 'awaiting_pricing' && (
                            <Tooltip title="Lock Price">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        updateRowPricing(params.row._id)
                                    }
                                    sx={actionButtonSx}
                                >
                                    <LockIcon
                                        sx={{ fontSize: 16, color: '#ff8656' }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}

                        {status === 'locked' && (
                            <Tooltip title="Unlock Link">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        unlockPaymentQueue(params.row._id)
                                    }
                                    sx={actionButtonSx}
                                >
                                    <LockOpenIcon
                                        sx={{ fontSize: 16, color: '#24c82f' }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}

                        {status === 'payment_generated' && paymentLinkId ? (
                            <Tooltip title="Create Follow up Payment">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        createFollowupPayment(params.row._id)
                                    }
                                    disabled={loadingActions.followup}
                                    sx={actionButtonSx}
                                >
                                    {loadingActions.followup ===
                                    params.row._id ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <ReplayIcon
                                            sx={{
                                                fontSize: 16,
                                                color: '#234c4c',
                                            }}
                                        />
                                    )}
                                </IconButton>
                            </Tooltip>
                        ) : null}

                        <Tooltip title="Delete Row">
                            <IconButton
                                size="small"
                                onClick={() =>
                                    deletePaymentQueue(params.row._id)
                                }
                                sx={actionButtonSx}
                            >
                                <DeleteForeverIcon
                                    sx={{ fontSize: 16, color: '#f08080' }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ];

    // Built from the rows actually in the queue, not a full POC roster, so
    // every option is guaranteed to match at least one row.
    const pocOptions = useMemo(() => {
        const byKey = new Map();
        rows.forEach((row) => {
            const key = pocKeyOf(row);
            if (!key || byKey.has(key)) return;
            byKey.set(key, row.pocData?.fullName || 'Unnamed POC');
        });
        return Array.from(byKey, ([value, label]) => ({ value, label })).sort(
            (a, b) => a.label.localeCompare(b.label)
        );
    }, [rows]);

    // Only offered when such rows exist — otherwise it's a choice that can
    // only ever empty the table.
    const hasUnassignedPoc = useMemo(
        () => rows.some((row) => !pocKeyOf(row)),
        [rows]
    );

    const filteredRows = rows.filter((row) => {
        const searchResult = searchText.toLowerCase().trim();
        const matchesSearch =
            !searchResult || row.username?.toLowerCase().includes(searchResult);
        // row.vendorData?.name?.toLowerCase().includes(searchResult) ||
        // row.pocData?.fullName?.toLowerCase().includes(searchResult);
        const matchesStatus = !statusFilter || row.status === statusFilter;
        const rowPocKey = pocKeyOf(row);
        const matchesPoc =
            !pocFilter ||
            (pocFilter === UNASSIGNED_POC ? !rowPocKey : rowPocKey === pocFilter);
        return matchesSearch && matchesStatus && matchesPoc;
    });
    async function handleCreateCommonLink() {
        try {
            const queue = rows.find((row) => row._id === selectedRows[0]);

            if (!queue) {
                return;
            }

            const confirmation =
                await CustomConfirms.showCommonPaymentConfirmation(
                    selectedRows.length,
                    queue?.vendorData?.name
                );

            console.log(`confirmation: `, confirmation);

            if (!confirmation.proceed) {
                return;
            }

            const response =
                await portalpaymentlink.createCombinedLinkForCreator({
                    queueIds: selectedRows,
                    isGst: confirmation.isGst,
                });

            if (response?.success) {
                Swal.fire({
                    title: 'Create Common Payment Link',
                    text: 'Common Payment Link has been generated',
                    icon: 'success',
                });

                window.location.reload();
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'There has been an error in creating combined payment link';
            Swal.fire({
                title: 'Create Common Payment Link',
                text: errorMessage,
                icon: 'error',
            });
        }
    }

    function setLoading(action, value) {
        setLoadingActions((prev) => ({
            ...prev,
            [action]: value,
        }));
    }

    const hasSelection = selectedRows.length > 0;

    return (
        <Box>
            {/* One toolbar: filters read first (left), bulk actions where users
                expect them (right) — was two separate stacked boxes before. */}
            <Stack
                component={Paper}
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={1.5}
                sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1.5, sm: 2 },
                    mb: 2,
                    borderRadius: 2,
                }}
            >
                {/* Search + Status filter */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                    <SearchField
                        placeholder="Search by Creator"
                        value={searchText}
                        onChange={setSearchText}
                    />

                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        // Same treatment as the POC filter beside it — without
                        // this the box reads blank while "All Statuses" is the
                        // active choice, and the two controls would no longer
                        // match each other.
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            displayEmpty: true,
                            renderValue: (value) =>
                                value
                                    ? STATUS_CONFIG[value]?.label || value
                                    : 'All Statuses',
                        }}
                        sx={{
                            width: { xs: '100%', sm: 170 },
                            ...filterSelectSx,
                        }}
                    >
                        <MenuItem value="">All Statuses</MenuItem>

                        {Object.entries(STATUS_CONFIG).map(
                            ([value, config]) => (
                                <MenuItem key={value} value={value}>
                                    {config.label}
                                </MenuItem>
                            )
                        )}
                    </TextField>

                    <TextField
                        select
                        size="small"
                        label="POC"
                        value={pocFilter}
                        onChange={(e) => setPocFilter(e.target.value)}
                        // A Select treats '' as "nothing picked" and renders a
                        // blank box, so the "All POCs" item never showed even
                        // though it is the active choice. displayEmpty plus an
                        // explicit renderValue makes the current state always
                        // readable; the label is pinned shrunk so it can't sit
                        // on top of the text it would otherwise overlap.
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            displayEmpty: true,
                            renderValue: (value) => {
                                if (!value) return 'All POCs';
                                if (value === UNASSIGNED_POC) return 'No POC';
                                return (
                                    pocOptions.find((poc) => poc.value === value)
                                        ?.label || value
                                );
                            },
                        }}
                        sx={{
                            width: { xs: '100%', sm: 190 },
                            ...filterSelectSx,
                        }}
                    >
                        <MenuItem value="">All POCs</MenuItem>

                        {pocOptions.map((poc) => (
                            <MenuItem key={poc.value} value={poc.value}>
                                {poc.label}
                            </MenuItem>
                        ))}

                        {hasUnassignedPoc && (
                            <MenuItem value={UNASSIGNED_POC}>No POC</MenuItem>
                        )}
                    </TextField>
                </Stack>

                {/* Selection count + bulk actions */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={1.5}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                    {hasSelection && (
                        <Chip
                            label={`${selectedRows.length} selected`}
                            size="small"
                            sx={{
                                alignSelf: { xs: 'flex-start', sm: 'center' },
                                fontWeight: 600,
                                color: '#832ccf',
                                bgcolor: 'rgba(131,44,207,0.1)',
                            }}
                        />
                    )}

                    {/* gap + wrap (not spacing) so a fourth action can drop to a
                        second line instead of pushing the page into horizontal scroll. */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={0}
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            flexWrap: 'wrap',
                            gap: 1,
                        }}
                    >
                        <Button
                            size="small"
                            sx={{
                                textTransform: 'capitalize',
                                fontSize: { xs: 12, sm: 14 },
                                background: '#832ccf',
                                width: { xs: '100%', sm: 'auto' },
                                whiteSpace: 'nowrap',
                            }}
                            variant="contained"
                            disabled={checkDisabledState('assign_vendor')}
                            onClick={() => setVendorAssignModal(true)}
                        >
                            Assign Vendor
                        </Button>

                        <Button
                            size="small"
                            sx={{
                                textTransform: 'capitalize',
                                fontSize: { xs: 12, sm: 14 },
                                background: '#339df3',
                                width: { xs: '100%', sm: 'auto' },
                                whiteSpace: 'nowrap',
                            }}
                            variant="contained"
                            disabled={checkDisabledState(
                                'create_payment_links'
                            )}
                            onClick={generatePaymentLinks}
                        >
                            Create Payment Links
                        </Button>

                        <Button
                            size="small"
                            sx={{
                                textTransform: 'capitalize',
                                fontSize: { xs: 12, sm: 14 },
                                background: '#832ccf',
                                width: { xs: '100%', sm: 'auto' },
                                whiteSpace: 'nowrap',
                            }}
                            variant="contained"
                            disabled={checkDisabledState(
                                'common_payment_link'
                            )}
                            onClick={handleCreateCommonLink}
                        >
                            Create Common Payment Link
                        </Button>

                        {chatByQueueId.size > 0 && (
                            <Button
                                size="small"
                                sx={{
                                    textTransform: 'capitalize',
                                    fontSize: { xs: 12, sm: 14 },
                                    color: '#1FAF57',
                                    borderColor: '#25D366',
                                    width: { xs: '100%', sm: 'auto' },
                                    whiteSpace: 'nowrap',
                                }}
                                variant="outlined"
                                startIcon={<WhatsAppIcon />}
                                onClick={() =>
                                    navigate(
                                        `/payments/campaigns/details/${queue?.[0]?.campaignId}/whatsapp`
                                    )
                                }
                            >
                                View WhatsApp Chats
                            </Button>
                        )}

                        {hasPermission('whatsapp_campaigns') && (
                            <Button
                                size="small"
                                sx={{
                                    textTransform: 'capitalize',
                                    fontSize: { xs: 12, sm: 14 },
                                    background: '#25D366',
                                    '&:hover': { background: '#1FAF57' },
                                    width: { xs: '100%', sm: 'auto' },
                                    whiteSpace: 'nowrap',
                                }}
                                variant="contained"
                                disabled={checkDisabledState('send_whatsapp')}
                                onClick={() => setWhatsappModalOpen(true)}
                            >
                                Send link on WhatsApp
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Stack>
            <Box
                sx={{
                    width: '100%',overflowX: 'auto'}}
                component={Paper}
            >
                <Box
                sx={{minWidth: { xs: 1200, sm: 1400 }}}>
                    <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    rowHeight={36}
                    columnHeaderHeight={42}
                    density="standard"
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 100,
                            },
                        },
                    }}
                    pageSizeOptions={[20, 50, 100]}
                    getRowId={(row) => row._id}
                    disableRowSelectionOnClick={true}
                   sx={{
                    height: {
                        xs: 'calc(100vh - 280px)',
                        sm: 'calc(100vh - 240px)',
                    },
                    minHeight: 400,
                    border: 0,
                    fontSize: {xs:11,sm:12},
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f8fafc',
                        borderRight: 1,
                        borderRightColor: '#c2c8c9',
                    },
                    '& .MuiDataGrid-cell': {
                        py: 1,
                        borderRight: 1,
                        borderRightColor: '#c2c8c9',
                    },
                }}
                    disableColumnFilter
                    disableColumnMenu
                    showColumnVerticalBorder
                />
                </Box>
           
            </Box>

            <AssignVendorModal
                open={vendorAssignModal}
                onClose={() => setVendorAssignModal(false)}
                handleAssignVendor={assignVendorToQueue}
            />

            <PaymentLinkWhatsAppModal
                open={whatsappModalOpen}
                onClose={() => setWhatsappModalOpen(false)}
                campaignId={rows[0]?.campaignId}
                selectedQueueIds={selectedRows}
            />

            <Dialog
                open={datePickerModal.open}
                onClose={() =>
                    !datePickerModal.saving &&
                    setDatePickerModal({
                        open: false,
                        queueId: null,
                        selectedDate: new Date(),
                        saving: false,
                    })
                }
                maxWidth="xs"
                fullWidth
                TransitionComponent={Grow}
                transitionDuration={220}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <DateRangeIcon color="primary" />
                        <Typography variant="h6">Edit Payable Date</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        type="date"
                        value={
                            datePickerModal.selectedDate
                                .toISOString()
                                .split('T')[0]
                        }
                        onChange={(e) =>
                            setDatePickerModal({
                                ...datePickerModal,
                                selectedDate: new Date(e.target.value),
                            })
                        }
                        fullWidth
                        sx={{
                            mt: 1,
                            transition: 'box-shadow 0.2s ease',
                            '&:focus-within': {
                                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.15)',
                                borderRadius: '8px',
                            },
                        }}
                        disabled={datePickerModal.saving}
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions sx={{px: { xs: 2, sm: 3 },pb: 2,gap: 1,flexDirection: { xs: 'column-reverse', sm: 'row' }}}>
                    <Button
                        onClick={() =>
                            setDatePickerModal({
                                open: false,
                                queueId: null,
                                selectedDate: new Date(),
                                saving: false,
                            })
                        }
                        variant="outlined"
                        disabled={datePickerModal.saving}
                        sx={{
                            transition: 'transform 0.15s ease',
                            '&:hover': { transform: 'translateY(-1px)' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={savePayableDate}
                        variant="contained"
                        color="primary"
                        disabled={datePickerModal.saving}
                        startIcon={
                            datePickerModal.saving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <DateRangeIcon />
                            )
                        }
                        sx={{
                            transition:
                                'transform 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow:
                                    '0 4px 12px rgba(25, 118, 210, 0.35)',
                            },
                        }}
                    >
                        {datePickerModal.saving ? 'Saving...' : 'Save Date'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default QueueStatus;
